import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";

import { PageHeader, type PageHelp } from "@/components/shared/page-help";
import { type RuleSurfaceRow, RuleSurfaceSummary } from "@/components/shared/rule-surface-summary";
import { SingleStageRuntimePanel } from "@/components/shared/single-stage-runtime-panel";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/quality")({
	component: QualityRoute,
});

const QUALITY_DECISION_ROWS = [
	{
		decision: "PASS",
		meaning: "回复可放行",
		evidence: "有意图、方案、证据或明确无证据说明，未触发 R2 风险。",
		next: "进入端到端结论或评测运行。",
	},
	{
		decision: "REVISE",
		meaning: "需要改写",
		evidence: "表达不清、证据引用弱、话术不符合客服口径，但不需要阻断。",
		next: "回到提示词与话术或处理方案优化。",
	},
	{
		decision: "BLOCK",
		meaning: "必须阻断",
		evidence: "无依据承诺、隐私/权限越界、真实写操作未确认或 R2 保护不满足。",
		next: "转人工或进入保护链路。",
	},
	{
		decision: "HANDOFF",
		meaning: "转人工",
		evidence: "客户情绪、投诉、复杂售后或系统无法给出可靠结论。",
		next: "进入评测运营和问题样本人工复核。",
	},
];

const QUALITY_INPUT_ROWS = [
	{
		label: "当前只读运行事实",
		value: "quality_mode、quality_completed、final_action_type",
	},
	{
		label: "证据来源",
		value: "意图、处理方案、工具结果、知识命中、最终回复",
	},
	{
		label: "样本入口",
		value: "质量策略样本：P10TA001/P10TA005/P10TA007/P10TA010",
	},
	{
		label: "当前边界",
		value: "复用运行质量事件，不自动阻断客户回复，不写入问题闭环",
	},
];

const QUALITY_RULE_ROWS: RuleSurfaceRow[] = [
	{
		name: "质量运行事件",
		source: "Admin Run 的 quality_completed",
		edit: "当前不可编辑",
		effect: "主链路生效",
		verify: "跑质量策略或链路测试台，看质量判定和 final_action_type",
		tone: "live",
	},
	{
		name: "基础质量规则",
		source: "Q-fast + grounding starter",
		edit: "当前不可配置",
		effect: "基础版生效",
		verify: "看空回复、敏感信息、证据支撑和阻断原因",
		tone: "partial",
	},
	{
		name: "PASS / REVISE / BLOCK / HANDOFF",
		source: "页面判定口径 + 运行结果",
		edit: "当前只展示口径",
		effect: "人工复核",
		verify: "失败后进入评测运营定位责任资产",
		tone: "readonly",
	},
];

function QualityRoute() {
	return (
		<div className="mx-auto flex max-w-7xl flex-col gap-5">
			<PageHeader
				moduleId="P10-S1-AB"
				title="质量策略"
				description="只读观测：用代表样本看质量判定（放行/改写/阻断/转人工）。本页不在此配置策略；运行开关（质量模式/写动作）在「接入状态」页。"
				help={qualityHelp}
			/>

			<RuleSurfaceSummary
				rows={QUALITY_RULE_ROWS}
				testId="quality-rule-surface-summary"
				description="这个页面先把质量结果讲清楚；具体策略编辑和自动问题闭环仍不是当前页面已上线能力。"
			/>

			<SingleStageRuntimePanel
				kind="quality"
				targetStage="quality_guard"
				sampleId="P10TA007"
				sampleMessage="帮我直接取消这个订单"
				expectedIntent="order_cancel"
				expectedPlaybook="PB04_cancel"
				assetName="质量判定 / 运行质量事件 / 风险边界"
				boundary="当前复用运行质量事件做局部验证，不自动阻断客户回复、不自动生成问题。"
			/>

			<section
				className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]"
				data-testid="p10-s1z-quality-workbench"
			>
				<Card>
					<CardHeader>
						<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
							<div>
								<CardTitle className="text-lg">质量策略单环节测试</CardTitle>
								<CardDescription>
									先定义 PASS / REVISE / BLOCK / HANDOFF 四类判断、输入证据和验收边界；当前复用
									运行质量事件，不伪装成独立质量执行接口已上线。
								</CardDescription>
							</div>
							<StatusPill tone="neutral">可直接运行</StatusPill>
						</div>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid gap-3 md:grid-cols-2">
							{QUALITY_INPUT_ROWS.map((row) => (
								<div
									key={row.label}
									className="rounded-md border border-surface-border bg-surface-background p-3"
								>
									<div className="text-xs font-medium text-text-muted">{row.label}</div>
									<div className="mt-2 text-sm font-semibold leading-6 text-text-primary">
										{row.value}
									</div>
								</div>
							))}
						</div>

						<div className="overflow-x-auto rounded-md border border-surface-border">
							<table className="w-full min-w-[820px] border-collapse text-sm">
								<thead className="bg-surface-subtle text-left text-xs text-text-muted">
									<tr>
										<th className="px-3 py-2 font-medium">判定</th>
										<th className="px-3 py-2 font-medium">业务含义</th>
										<th className="px-3 py-2 font-medium">证据要求</th>
										<th className="px-3 py-2 font-medium">后续动作</th>
									</tr>
								</thead>
								<tbody>
									{QUALITY_DECISION_ROWS.map((row) => (
										<tr
											key={row.decision}
											className="border-t border-surface-border align-top"
											data-testid="quality-contract-decision-row"
										>
											<td className="px-3 py-3 font-mono text-xs font-semibold text-text-primary">
												{row.decision}
											</td>
											<td className="px-3 py-3 text-text-secondary">{row.meaning}</td>
											<td className="px-3 py-3 text-text-secondary">{row.evidence}</td>
											<td className="px-3 py-3 text-text-secondary">{row.next}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<div className="rounded-md border border-brand-warning/30 bg-brand-warning/10 p-3 text-sm leading-6 text-brand-warning">
							<div className="font-medium">当前边界</div>
							<p className="mt-1">
								质量策略能在链路测试台复用运行质量事件做单环节检查：不新增质量策略接口、不改主回复链路、不自动阻断客户回复、不自动生成问题、不写回回归集。
							</p>
						</div>
					</CardContent>
				</Card>

				<div className="space-y-4">
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">产品验收入口</CardTitle>
							<CardDescription>从合同、链路测试和评测运营三处看同一个边界。</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							<LinkButton to="/test-datasets?mock=1#p10-single-stage-quality-contract">
								查看样本合同
							</LinkButton>
							<LinkButton to="/pipeline-testbench?mock=1">跑端到端链路</LinkButton>
							<LinkButton to="/eval?mock=1#single-stage-candidate">看评测运营承接</LinkButton>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle className="text-lg">下一步接入条件</CardTitle>
							<CardDescription>满足这些条件后再从合同进入真实单环节运行。</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3 text-sm text-text-secondary">
							<ChecklistItem>Trace 能稳定返回 quality_completed 输入和输出。</ChecklistItem>
							<ChecklistItem>样本具备 expected_quality_decision。</ChecklistItem>
							<ChecklistItem>评测运营能承接失败责任资产和问题人工复核。</ChecklistItem>
						</CardContent>
					</Card>
				</div>
			</section>
		</div>
	);
}

function LinkButton({ to, children }: { to: string; children: string }) {
	return (
		<a
			href={to}
			className="flex items-center justify-between gap-2 rounded-md border border-surface-border bg-surface-background px-3 py-2 text-sm font-medium text-text-primary hover:bg-surface-subtle"
		>
			{children}
			<ArrowRight className="h-4 w-4 text-text-muted" aria-hidden="true" />
		</a>
	);
}

function ChecklistItem({ children }: { children: string }) {
	return (
		<div className="flex items-start gap-2">
			<CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-brand-primary" aria-hidden="true" />
			<span>{children}</span>
		</div>
	);
}

function StatusPill({ children, tone }: { children: string; tone: "warning" | "neutral" }) {
	const className =
		tone === "warning"
			? "border-brand-warning/30 bg-brand-warning/10 text-brand-warning"
			: "border-surface-border bg-surface-subtle text-text-secondary";
	return (
		<span
			className={`inline-flex w-fit rounded-md border px-2 py-1 text-xs font-medium ${className}`}
		>
			{children}
		</span>
	);
}

const qualityHelp: PageHelp = {
	title: "质量策略说明",
	description:
		"这里复用运行质量事件做单环节检查，帮助产品/运营看清哪些回复可放行、哪些要改写、阻断或转人工。",
	sections: [
		{
			title: "当前能看什么",
			items: [
				"能看 PASS、REVISE、BLOCK、HANDOFF 四类质量判断口径。",
				"能看质量判断需要依赖哪些运行事实和样本字段。",
				"能跳到样本资产和链路测试台核对同一套合同。",
			],
		},
		{
			title: "当前不能误判什么",
			items: [
				"当前不新增质量策略接口。",
				"当前不自动阻断客户回复，也不自动生成问题。",
				"真实质量执行和策略配置仍需要后续独立切片。",
			],
		},
	],
};
